import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { OrderFields, ResolutionId } from 'types/order'

import { selectOrderRead } from 'store/order/order.selectors'
import { selectToken } from 'store/users/users.selectors'

import { getResultPdf } from 'requests/order'

import Title from 'components/atoms/Title'
import Button from 'components/molecules/Button'
import { LoadingPlaceholder } from 'components/atoms/LoadingPlaceholder'

import { FINISH_RESOLUTIONS } from '../Header/components/ResolutionForm/constants'

import classes from './style.module.css'

const FAILED_TO_GENERATE_REPORT = 'Не удалось сформировать отчёт'

const isSafeUrl = (value: string) => Boolean(value && value.trim())

const stripPaddingTop = (value: string) => value.replace(/padding-top\s*:\s*[^;]+;?/gi, '').trim()

const sanitizeHtml = (rawHtml: string) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(rawHtml, 'text/html')

  // Не удаляем svg: иконки статуса в HTML-результате — inline SVG / data-uri.
  doc.querySelectorAll('script, iframe, object, embed, link, meta, base, math').forEach((node) => node.remove())

  doc.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attr) => {
      const { name, value } = attr
      const lowerName = name.toLowerCase()

      if (lowerName.startsWith('on') || lowerName === 'srcdoc' || lowerName === 'srcset') {
        element.removeAttribute(name)
        return
      }

      if (lowerName === 'style') {
        const cleanedStyle = stripPaddingTop(value)
        if (cleanedStyle) element.setAttribute(name, cleanedStyle)
        else element.removeAttribute(name)
      }

      if (lowerName === 'href' || lowerName === 'src' || lowerName === 'xlink:href' || lowerName === 'formaction') {
        if (!isSafeUrl(value)) element.removeAttribute(name)
      }
    })
  })

  Array.from(doc.querySelectorAll('style')).forEach((styleNode) => {
    const content = styleNode.textContent || ''
    const cleanedContent = stripPaddingTop(content)
    if (!cleanedContent) {
      styleNode.remove()
      return
    }
    const nextStyleNode = styleNode.cloneNode(true)
    nextStyleNode.textContent = cleanedContent
    styleNode.replaceWith(nextStyleNode)
  })

  doc.querySelectorAll('table').forEach((table) => {
    const rows = Array.from(table.querySelectorAll('tr'))
    rows.forEach((row) => {
      const firstCell = row.querySelector('th, td')
      const text = firstCell?.textContent?.trim() || ''
      if (/^\d+\.?$/.test(text)) row.classList.add('result-block-start')
    })
  })

  const styleTags = Array.from(doc.head?.querySelectorAll('style') || [])
    .map((node) => node.outerHTML)
    .join('')

  return `${styleTags}${doc.body?.innerHTML || ''}`
}

const AbsentReasonMessage: React.FC<{ reason: string }> = ({ reason }) => {
  if (reason === FAILED_TO_GENERATE_REPORT) {
    return <div className={classes.empty}>{reason}</div>
  }

  return (
    <div className={classes.empty}>
      <span className="font-weight-bold">Отчет не сформирован</span>
      {`: ${reason}`}
    </div>
  )
}

export const GeneratedResult: React.FC = () => {
  const form = useSelector(selectOrderRead)
  const token = useSelector(selectToken)
  const resultHtmlLink = form[OrderFields.RESULT]?.result_html ?? null
  const resultAbsentReason = form[OrderFields.RESULT]?.result_absent_reason || null
  const resolution = form[OrderFields.RESOLUTION]
  const orderNumber = String(form[OrderFields.ORDER_NUMBER] ?? '')

  const isClosed = FINISH_RESOLUTIONS.includes(String(resolution) as ResolutionId)
  const hasHtml = Boolean(resultHtmlLink)
  const hasAbsentReason = Boolean(resultAbsentReason)
  const shouldShow = isClosed && (hasHtml || hasAbsentReason)
  const canDownloadPdf = isClosed && hasHtml && Boolean(orderNumber)

  const [html, setHtml] = useState('')
  const [isPdfLoading, setIsPdfLoading] = useState(false)
  const [pdfError, setPdfError] = useState('')

  useEffect(() => {
    if (!resultHtmlLink) {
      setHtml('')
      return
    }

    let isActive = true
    setHtml('')

    fetch(resultHtmlLink)
      .then(async (response) => {
        if (!response.ok) throw new Error('Не удалось загрузить результат')
        const text = await response.text()
        if (isActive) setHtml(sanitizeHtml(text))
      })
      .catch(() => {
        if (isActive) setHtml('')
      })

    return () => {
      isActive = false
    }
  }, [resultHtmlLink, shouldShow])

  const handleDownloadPdf = useCallback(() => {
    if (!canDownloadPdf || !token) return

    setIsPdfLoading(true)
    setPdfError('')

    getResultPdf({ orderNumber }, token)
      .then((data: { link: string }) => {
        if (!data?.link) throw new Error('empty link')
        window.location.href = data.link
      })
      .catch(() => {
        setPdfError('Не удалось скачать PDF отчёт')
      })
      .finally(() => setIsPdfLoading(false))
  }, [canDownloadPdf, orderNumber, token])

  if (!shouldShow) return null

  return (
    <ul className="list-group row needs-validation">
      <li className={`list-group-item ${classes.resultItem}`}>
        <div className={classes.resultHeader}>
          <Title text="Результат" />
          {canDownloadPdf ? (
            <div className={classes.pdfActions}>
              <Button onClick={handleDownloadPdf} isLoading={isPdfLoading} variant="outline-primary" className={classes.pdfButton}>
                Скачать отчёт
              </Button>
              {pdfError ? <div className={classes.error}>{pdfError}</div> : null}
            </div>
          ) : null}
        </div>
        <div className={classes.resultContainer}>
          {hasHtml ? (
            <>
              {!html ? <LoadingPlaceholder /> : null}
              {html ? (
                // Required to render sanitized HTML result.
                // eslint-disable-next-line react/no-danger
                <div className={classes.html} dangerouslySetInnerHTML={{ __html: html }} />
              ) : null}
            </>
          ) : (
            <AbsentReasonMessage reason={resultAbsentReason} />
          )}
        </div>
      </li>
    </ul>
  )
}

export default GeneratedResult
